/**
 * <countdown-timer> — AYSOMART / ELLA revamp
 *
 * Counts down to a target datetime and renders Days / Hours / Mins / Secs.
 * Attributes:
 *   end-time   ISO 8601 datetime string (required), e.g. "2026-12-31T23:59:59"
 *   on-expire  "hide" (default) | "keep"  — what to do when it reaches zero
 *
 * Markup contract (filled by blocks/countdown-timer.liquid):
 *   [data-unit="days"], [data-unit="hours"], [data-unit="minutes"], [data-unit="seconds"]
 *   [data-countdown-expired]  — shown when finished
 */
class CountdownTimer extends HTMLElement {
  connectedCallback() {
    this.endTime = CountdownTimer.parseEndTime(this.getAttribute('end-time'));
    this.onExpire = this.getAttribute('on-expire') || 'hide';
    this.units = {
      days: this.querySelector('[data-unit="days"]'),
      hours: this.querySelector('[data-unit="hours"]'),
      minutes: this.querySelector('[data-unit="minutes"]'),
      seconds: this.querySelector('[data-unit="seconds"]'),
    };
    this.expiredEl = this.querySelector('[data-countdown-expired]');

    if (Number.isNaN(this.endTime)) {
      // Unparsable date in the block settings: never show a frozen 00:00:00:00.
      this.hidden = true;
      return;
    }

    this.tick();
    this.interval = setInterval(() => this.tick(), 1000);
  }

  /**
   * Parses "YYYY-MM-DDTHH:MM:SS" the same way on every browser. Safari and
   * Firefox reject out-of-range ISO dates (e.g. 2026-09-31) while Chrome rolls
   * them forward, so we build the Date from parts and let JS normalise it.
   * @param {string | null} value
   * @returns {number} epoch ms, or NaN
   */
  static parseEndTime(value) {
    if (!value) return NaN;
    const m = /^(\d{4})-(\d{1,2})-(\d{1,2})(?:[T ](\d{1,2}):(\d{2})(?::(\d{2}))?)?/.exec(value.trim());
    if (m) {
      const [, y, mo, d, h = '23', mi = '59', s = '59'] = m;
      return new Date(+y, +mo - 1, +d, +h, +mi, +s).getTime();
    }
    return new Date(value).getTime();
  }

  disconnectedCallback() {
    clearInterval(this.interval);
  }

  tick() {
    const remaining = this.endTime - Date.now();

    if (remaining <= 0) {
      clearInterval(this.interval);
      this.render(0, 0, 0, 0);
      this.finish();
      return;
    }

    const days = Math.floor(remaining / 86400000);
    const hours = Math.floor((remaining % 86400000) / 3600000);
    const minutes = Math.floor((remaining % 3600000) / 60000);
    const seconds = Math.floor((remaining % 60000) / 1000);
    this.render(days, hours, minutes, seconds);
  }

  render(d, h, m, s) {
    const pad = (n) => String(n).padStart(2, '0');
    if (this.units.days) this.units.days.textContent = pad(d);
    if (this.units.hours) this.units.hours.textContent = pad(h);
    if (this.units.minutes) this.units.minutes.textContent = pad(m);
    if (this.units.seconds) this.units.seconds.textContent = pad(s);
  }

  finish() {
    const timer = this.querySelector('[data-countdown-clock]');
    if (this.onExpire === 'hide') {
      if (timer) timer.hidden = true;
      if (this.expiredEl) this.expiredEl.hidden = false;
    }
  }
}

if (!customElements.get('countdown-timer')) {
  customElements.define('countdown-timer', CountdownTimer);
}
